"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Pillar = [string, string, string];

export function CompassInteractive({ pillars }: { pillars: Pillar[] }) {
  const [active, setActive] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!pillars?.length) return;
    
    // Auto-rotate the compass every 5 seconds if not hovered
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % pillars.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [pillars]);

  if (!pillars?.length) return null;

  return (
    <div className="compass-pro-wrap">
      {/* Background Liquid Glow */}
      <div className="compass-ambient-glow" />

      <div className="compass-dial-container">
        {/* The Outer Rotating Ring */}
        <div className="compass-dial-ring" />
        <div className="compass-dial-ring inner" />

        {/* The Nodes */}
        {mounted &&
          pillars.map(([letter], i) => {
            const angle = (i * Math.PI * 2) / 7 - Math.PI / 2;
            const left = 50 + Math.cos(angle) * 50;
            const top = 50 + Math.sin(angle) * 50;
            const isActive = active === i;

            return (
              <motion.button
                key={i}
                className={`compass-node ${isActive ? "active" : ""}`}
                style={{ left: `${left}%`, top: `${top}%` }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: isActive ? 1.15 : 1 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                onClick={() => setActive(i)}
                whileHover={{ scale: 1.1 }}
              >
                <div className="compass-node-bg" />
                <span className="compass-node-letter">{letter}</span>
                {isActive && (
                  <motion.div
                    layoutId="node-glow"
                    className="compass-node-active-glow"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}

        {/* Center Content */}
        <div className="compass-center-display">
          <AnimatePresence mode="wait">
            {(() => {
              const p = pillars[active];
              if (!p) return null;
              return (
                <motion.div
                  key={active}
                  className="compass-center-inner"
                  initial={{ opacity: 0, filter: "blur(10px)", scale: 0.9 }}
                  animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                  exit={{ opacity: 0, filter: "blur(10px)", scale: 1.1 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <div className="compass-center-letter">{p[0]}</div>
                  <h3 className="compass-center-name">{p[1]}</h3>
                  <p className="compass-center-desc">{p[2]}</p>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Mobile-only interactive list fallback/addition */}
      <div className="compass-mobile-list">
        {pillars.map(([letter, name, desc], i) => {
          const isActive = active === i;
          return (
            <div 
              key={i} 
              className={`compass-mob-card ${isActive ? 'active' : ''}`}
              onClick={() => setActive(i)}
            >
              <div className="compass-mob-head">
                <div className="compass-mob-letter">{letter}</div>
                <div className="compass-mob-name">{name}</div>
              </div>
              <AnimatePresence>
                {isActive && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="compass-mob-body"
                  >
                    <p>{desc}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
